<script setup lang="ts">
import Skeleton from 'primevue/skeleton';
import { navigateToUrl } from 'single-spa';
import { computed, ref, shallowRef } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';
import { getNestedPropertyValue } from 'lib/utils/object.util';
import { formatUserName } from 'lib/utils/textFormatter.util';

import { UserNameProps } from './UserName.vue.d';
import Icon from '../icon/Icon.vue';
import Image from '../image/Image.vue';
import OverlayPanel from '../overlaypanel/OverlayPanel.vue';
import OverlayPanelClass from '../overlaypanel/OverlayPanel.vue.d';

const props = withDefaults(defineProps<UserNameProps>(), {
  fetchDetailOnPanelShow: true,
  showUserName: undefined,
  formatUserName: undefined,
});

const { locale, defaultProps } = useWangsVueConfig('UserName');

const config = computed(() => mergePropsWithDefaults(props, defaultProps));

const loadingUser = shallowRef<boolean>(false);
const fullUserObject = shallowRef<typeof props.user>();
const showPreview = shallowRef<boolean>(false);
const miniProfile = ref<OverlayPanelClass>();

const userDisplayName = computed<string>(
  () =>
    getNestedPropertyValue(
      props.user ?? {},
      config.value.userNameField,
    ) as string,
);

const truncatedUserName = computed<string>(() => {
  return config.value.formatUserName
    ? formatUserName(userDisplayName.value)
    : userDisplayName.value;
});

const adjustPosition = async (): Promise<void> => {
  if (!fullUserObject.value && props.fetchDetailOnPanelShow) {
    try {
      loadingUser.value = true;
      const data = await config.value.getUserDetail?.(props.user?._id);
      fullUserObject.value = data;
    } catch (error) {
      console.error(error);
    } finally {
      loadingUser.value = false;
    }
  } else if (!props.fetchDetailOnPanelShow) {
    fullUserObject.value = props.user;
  }
};
</script>

<template>
  <span v-if="Object.keys(props.user ?? {}).length < 1"> - </span>

  <span
    v-else
    @click="miniProfile?.toggle"
    v-bind="$preset.username.root"
    data-wv-name="username"
    data-wv-section="root"
  >
    <Image
      v-if="config.type == 'picture'"
      :preview="userDisplayName !== undefined"
      :src="
        getNestedPropertyValue(
          props.user ?? {},
          config.profilePictureField,
        ) as string
      "
      @click.stop="if (!userDisplayName) miniProfile?.toggle($event);"
      rounded
      size="small"
    />

    <Icon
      v-else
      class="text-general-300 dark:text-grayscale-700 text-base"
      icon="user"
    />

    <span
      v-if="config.showUserName"
      v-tooltip.top="
        truncatedUserName === userDisplayName ? undefined : userDisplayName
      "
      v-bind="$preset.username.displayname({ props })"
      data-wv-section="displayname"
    >
      {{ truncatedUserName }}
    </span>

    <OverlayPanel
      ref="miniProfile"
      v-if="Object.keys(props.user ?? {}).length > 1"
      :dismissable="!showPreview"
      @show="adjustPosition"
    >
      <div
        v-bind="$preset.username.panel.contentwrapper"
        data-wv-section="contentwrapper"
      >
        <div
          v-bind="$preset.username.panel.background"
          data-wv-section="background"
        />

        <Image
          :src="
            fullUserObject?.profilePicture ?? fullUserObject?.profilePictureBig
          "
          :width="60"
          @hide="showPreview = false"
          @show="showPreview = true"
          class="w-[60px] h-[60px]"
          rounded
        />

        <Skeleton v-if="loadingUser" height="29px" width="100px" />

        <div v-else v-bind="$preset.username.panel.detailwrapper">
          <h4
            v-bind="$preset.username.panel.username"
            data-wv-section="username"
          >
            {{ fullUserObject?.fullName }}
            <template v-if="fullUserObject?.nickName">
              ({{ fullUserObject?.nickName }})
            </template>
          </h4>

          <span
            v-bind="$preset.username.panel.useremail"
            data-wv-section="useremail"
          >
            {{ fullUserObject?.email }}
          </span>
        </div>

        <Skeleton v-if="loadingUser" height="12px" width="60px" />

        <span
          v-else
          v-bind="$preset.username.panel.positiondivision"
          data-wv-section="positiondivision"
        >
          {{ fullUserObject?.division }}
          /
          {{ fullUserObject?.position }}
        </span>

        <Skeleton v-if="loadingUser" width="75px" />

        <a
          v-else
          @click.stop="
            navigateToUrl(
              config.detailUserPath.replace('{userId}', props.user?._id),
            )
          "
          v-bind="$preset.username.panel.userdetaillink"
          data-wv-section="userdetaillink"
        >
          {{ locale.detailUserAnchorText }}
        </a>
      </div>
    </OverlayPanel>
  </span>
</template>
